import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { CustomerBillingPermission, GalleryDto, GalleryFeedbackOptions } from '@nusign/picflow-backend-types';
import { Observable, of, shareReplay, tap } from 'rxjs';
import Stripe from 'stripe';
import { BillingScope, billingScopes, getBytesFromGigabytes, productScopes } from '../billing-consts';
import { allFeedbackOptions, feedbackOptionToScopeMapping } from '../consts';
import { GROWTHBOOK } from '../growthbook';
import { GalleryAccessLevel } from '../lib/access-level';
import { PICFLOW_CONFIG } from '../picflow-config';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private state = inject(StateService);
  private http = inject(HttpClient);
  private config = inject(PICFLOW_CONFIG);
  private observableLoader = inject(ObservableDataLoaderService);
  private currentBillingPermissions = signal<CustomerBillingPermission>(undefined);
  private growthBook = inject(GROWTHBOOK);
  private rolePermisions: string[];

  public canDisplayElement(scope: BillingScope): boolean {
    if (scope === 'gallery:other-source-upload') return true; // temp, till backend deployment
    const scopes = this.currentBillingPermissions()?.scopes;
    if (!scopes) return false;
    return scopes.includes(scope);
  }

  public getBillingScopes() {
    return this.currentBillingPermissions().scopes;
  }
  public getBillingScopesForProduct(product: string) {
    return productScopes[product];
  }

  public getMinPlanForScope(scope: BillingScope): Observable<string> {
    // will need to refactor this to be async once the backend has an endpoint to fetch this data

    const productsThatIncludeScope = Object.keys(productScopes).filter(planName =>
      productScopes[planName].includes(scope)
    );

    const defaultProducts = [
      ...this.growthBook
        .getFeatureValue('all-listed-products', [])
        .map((product: string) => product.toLowerCase())
        .filter(product => product !== 'free' && product !== 'enterprise'),
      'enterprise',
    ];

    defaultProducts.sort((a, b) => {
      const scopesA = productScopes[a];
      const scopesB = productScopes[b];
      return scopesA.length - scopesB.length;
    });

    return of(defaultProducts.find(productName => productsThatIncludeScope.includes(productName)));
  }

  public shouldRedirectToPricingPage(tenantId: string): boolean {
    if (!tenantId || tenantId === this.state.activeTenant?.id) {
      return ['admin', 'owner'].includes(this.state.activeTenantRole);
    }
    return false;
  }

  public hasRolePermission(permission: string): boolean {
    if (!this.rolePermisions) return false;
    return this.rolePermisions.includes(permission);
  }

  public setRolePermissions(permissions: string[]) {
    this.rolePermisions = permissions;
  }

  public loadTenantBillingPermissions(tenantId: string) {
    return this.getTenantBillingPermissions(tenantId).pipe(
      tap(permissions => {
        this.currentBillingPermissions.set(permissions);
      }),
      shareReplay(1)
    );
  }

  getStorageForPrice = (price: Stripe.Price): bigint => {
    const gb = price.metadata.storage_capacity || (price.product as Stripe.Product).metadata.storage_capacity;
    if (!gb) return 0n;
    return getBytesFromGigabytes(BigInt(gb));
  };

  public getTenantBillingPermissions(tenantId: string) {
    return this.observableLoader.get(`tenant_permissions_${tenantId}`, () => {
      return this.http.get<CustomerBillingPermission & { scopes: BillingScope[] }>(
        `${this.config.apiEndpoint}/tenants/${tenantId}/billing/permissions`
      );
    });
  }

  public getAllowedFeedbackOptions(accessLevel: GalleryAccessLevel): GalleryFeedbackOptions[] {
    if (accessLevel === 'view_only' || accessLevel === 'no_access') return [];
    const scopes = this.currentBillingPermissions().scopes;
    return allFeedbackOptions.filter(option => scopes.includes(feedbackOptionToScopeMapping[option]));
  }

  public getAllowedAndEnabledFeedbackOptions(
    gallery: GalleryDto,
    accessLevel: GalleryAccessLevel
  ): GalleryFeedbackOptions[] {
    if (accessLevel === 'view_only' || accessLevel === 'no_access') return [];
    const options = gallery.enabled_feedback_options;
    return this.getAllowedFeedbackOptions(accessLevel).filter(option => {
      return options.includes(option);
    });
  }

  public isMax() {
    return this.canDisplayElement(billingScopes.asset.customLabels);
  }
}
